import { bulbMedium, femaleQuestionMedium } from '@/assets/icons';

const trs = {
  W5H1_TITLE: `QQOQCCP`,
  W5H1_WHAT_EXAMPLES: `De quoi s'agit-il ?\nQuel est l'état de la situation ?\nQuelles en sont les conséquences ?\nQuels sont les risques ?`,
  W5H1_WHO_EXAMPLES: `Qui est concerné ?\nQui a le problème ?\nQui est concerné par les conséquences du problème ?\nQui peut être affecté par la résolution ?`,
  W5H1_WHERE_EXAMPLES: `Où le problème est-il survenu ?\nQuels lieux sont impliqués ?\nDans quel atelier, sur quelle machine ?\nQuelles sont les conditions environnementales ?`,
  W5H1_WHEN_EXAMPLES: `Depuis quand avez-vous ce problème ?\nQuand apparaît-il ?\nQuand le problème a-t-il été découvert ?\nQuelle est sa fréquence ?\nQuand se produit le risque ?`,
  W5H1_HOW_EXAMPLES: `Comment le problème arrive-t-il ?\nDans quelles conditions ou circonstances ?\nComment procède-t-on ?\nY a-t-il des méthodes à utiliser ?\nPar quel moyen commencer la résolution, quelles procédures ?`,
  W5H1_HOWMUCH_EXAMPLES: `Quelle quantité d'eau inonde l'atelier ?\nCombien de défauts sont sortis avant que l'on ne détecte le problème ?`,
  W5H1_WHY_EXAMPLES: `Pourquoi est-ce un problème ?`,
};

const W5H1 = `
<p>
  La méthode QQOQCCP permet d'avoir des informations élémentaires mais suffisantes sur toutes les dimensions du problème.
  À l'aide de cet outil, chacun peut participer à l'identification complète de tous les aspects essentiels du sujet.
  <br>
  Elle adopte une démarche d'analyse critique constructive basée sur le questionnement systématique.
  <br><b>QQOQCCP</b> : <b>Q</b>uoi ? <b>Q</b>ui ? <b>O</b>ù ? <b>Q</b>uand ? <b>C</b>omment ? <b>C</b>ombien ? <b>P</b>ourquoi ?
</p>
<br>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Ces questions sont trop simples, on ne peut pas décrire un problème précisément de cette manière.</span>
</div>
<br>
<p>
  Plus on utilise cet outil, plus on remarque que chacune de ces questions simples en cache plusieurs autres.
  Permettez-moi de rentrer dans les détails :
</p>
<h2 class="basic-sub-title">
  @:message.WHAT ?
</h2>
<p>
  On pose cette question pour une meilleure description du problème.
  Toutes les manifestations physiques du problème peuvent être décrites ici.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Exemples :</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_WHAT_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  @:message.WHO ?
</h2>
<p>
  Cette question permet à chacun de comprendre qui est concerné par le problème.
  Identifier les différents acteurs rend la résolution de problème plus efficace.
  Et il faut même voir un peu plus loin :
  Savoir quelle personne aller consulter pour contrôler la véracité des causes ou l'efficacité des actions, par exemple, est essentiel.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Exemples :</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_WHO_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  @:message.WHERE ?
</h2>
<p>
  Cette question concerne la description des lieux. Le problème peut en effet survenir
  à un endroit pour une raison et à un autre endroit pour une autre. Il ne s'agit pas uniquement
  d'identifier le bâtiment mais d'identifier toutes les caractéristiques du lieu qui peuvent
  impacter le problème, comme la matière du sol, ou encore le type d'éclairage.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Exemples :</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_WHERE_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  @:message.WHEN ?
</h2>
<p>
  On pose cette question pour bien définir les temps. Ce qui vous aidera à mettre en
  relation les problèmes qui surviennent dans les mêmes circonstances temporelles, ou à la
  même fréquence.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Exemples :</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_WHEN_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  @:message.HOW ?
</h2>
<p>
  Les réponses à cette question permettront une meilleure compréhension de la
  manière ou de la méthode pour trouver et résoudre ce problème.
  <br>
  Il s'agit là d'être exhaustif. S'il est nécessaire de reproduire 10 étapes pour que le problème survienne,
  les 10 étapes doivent être décrites avec précision. Identifier la procédure de reproduction
  d'un problème est essentiel pour commencer à en déterminer les causes.
  <br>
  Cette étape est également un outil d'élimination de la récurrence très efficace, et
  il suffit souvent de reproduire la procédure de reproduction d'un problème pour en détecter d'autres.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Exemples :</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_HOW_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  @:message.HOW_MUCH ?
</h2>
<p>
  Cela permet de quantifier tout ce qui concerne le problème. Cette question vient compléter
  le "@:message.HOW" en mettant en évidence des notions quantifiables sur les circonstances d'apparition
  du problème.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Exemples :</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_HOWMUCH_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  @:message.WHY ?
</h2>
<p>
  Cette question est parfois délicate. Il ne faut pas se demander "Pourquoi le problème arrive ?",
  mais plutôt "Pourquoi cette situation est un problème ?". Faisons un bref rappel de notre cours sur
  "@:sensei.SPOT_PB_TITLE" : @:sensei.WHAT_IS_A_PB
  Alors, quelle est la situation idéale ?
  <br/>
</p>
<br/>
<p>
  Pensez à toujours bien compléter votre QQOQCCP avant d'entamer la suite de la résolution.
</p>
`;


export default {
  ...trs,
  W5H1,
};
